// src/contexts/NotificationContext.tsx - FIXED VERSION
import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'
import { useAuth } from './AuthContext'

// ✅ API URL from environment
const API_URL = import.meta.env.VITE_API_URL || 'https://emailai-backend-658e.onrender.com/api'

interface NotificationPreferences {
  campaignPerformance: boolean
  weeklyDigest: boolean
  upgradeReminders: boolean
  productUpdates: boolean
  weeklyTips: boolean
}

interface Notification {
  id: string
  type: string
  title: string
  message: string
  read: boolean
  createdAt: string
}

interface NotificationContextType {
  preferences: NotificationPreferences
  notifications: Notification[]
  unreadCount: number
  updatePreference: (key: keyof NotificationPreferences, value: boolean) => Promise<void>
  markAsRead: (id: string) => Promise<void>
  fetchNotifications: () => Promise<void>
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

const defaultPreferences: NotificationPreferences = {
  campaignPerformance: true,
  weeklyDigest: true,
  upgradeReminders: true,
  productUpdates: false,
  weeklyTips: true
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [preferences, setPreferences] = useState<NotificationPreferences>(defaultPreferences)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [csrfToken, setCsrfToken] = useState<string>('')

  const unreadCount = notifications.filter(n => !n.read).length

  useEffect(() => {
    const token = localStorage.getItem('accessToken')
    if (!token) return
    const fetchCsrfToken = async () => {
      try {
        // ✅ FIXED: Removed duplicate /api
        const response = await fetch(`${API_URL}/csrf-token`, { credentials: 'include' })
        const data = await response.json()
        setCsrfToken(data.csrfToken)
      } catch {}
    }
    fetchCsrfToken()
  }, [])

  useEffect(() => {
    if (!user) return
    const token = localStorage.getItem('accessToken')
    if (!token) return
    loadPreferences()
    fetchNotifications()
  }, [user])

  const loadPreferences = () => {
    const saved = localStorage.getItem(`notifications_${user?._id || user?.id}`)
    if (saved) {
      try {
        setPreferences(JSON.parse(saved))
      } catch {
        setPreferences(defaultPreferences)
      }
    }
  }

  const savePreferences = (newPrefs: NotificationPreferences) => {
    localStorage.setItem(`notifications_${user?._id || user?.id}`, JSON.stringify(newPrefs))
    setPreferences(newPrefs)
  }

  // ✅ Update preference with API sync
  const updatePreference = useCallback(async (key: keyof NotificationPreferences, value: boolean) => {
    const newPrefs = { ...preferences, [key]: value }
    savePreferences(newPrefs)
    
    try {
      const token = localStorage.getItem('accessToken')
      // ✅ FIXED: Removed duplicate /api
      await fetch(`${API_URL}/user/notification-preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify({ [key]: value })
      })
    } catch (error) {
      console.error('Failed to sync preference:', error)
    }
  }, [preferences, csrfToken])

  // ✅ Fetch notifications with proper URL
  const fetchNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken')
      // ✅ FIXED: Removed duplicate /api
      const response = await fetch(`${API_URL}/notifications`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include'
      })
      if (response.ok) {
        const data = await response.json()
        setNotifications(data.notifications || [])
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
    }
  }, [csrfToken])

  // ✅ Mark as read with proper URL
  const markAsRead = useCallback(async (id: string) => {
    try {
      const token = localStorage.getItem('accessToken')
      // ✅ FIXED: Removed duplicate /api
      const response = await fetch(`${API_URL}/notifications/${id}/read`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include'
      })
      if (response.ok) {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
      }
    } catch (error) {
      console.error('Failed to mark as read:', error)
    }
  }, [csrfToken])

  return (
    <NotificationContext.Provider value={{
      preferences,
      notifications,
      unreadCount,
      updatePreference,
      markAsRead,
      fetchNotifications
    }}>
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider')
  }
  return context
}